/**
 * HTML date inputs and Firestore "calendar" fields should use local date components,
 * not UTC parsing (`new Date('YYYY-MM-DD')`) or `toISOString().slice(0, 10)` for values,
 * or dates shift by one day in many timezones.
 */

/** Parse `<input type="date" />` value (YYYY-MM-DD) as a local calendar date at noon (stable vs DST). */
export function parseDateInputString(value: string): Date {
  const parts = value.trim().split('-').map((x) => parseInt(x, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y == null || m == null || d == null || Number.isNaN(y + m + d)) {
    return new Date(NaN);
  }
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

export function parseOptionalDateInput(value: string | undefined | null): Date | undefined {
  if (value == null || String(value).trim() === '') return undefined;
  const parsed = parseDateInputString(String(value));
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/** Bind a `Date` to `<input type="date" value={...} />` using local Y-M-D (not `toISOString()`). */
export function formatDateForInput(value: Date | undefined): string {
  if (value == null || !(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Display a calendar date in the user’s locale (numeric month/day). */
export function formatCalendarDateLabel(value: Date | undefined): string {
  if (value == null || !(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  return value.toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' });
}
